import colors from '@monorepo-zazuu/shared/constants/colors'
import IAppointment from '@monorepo-zazuu/shared/interfaces/IAppointment'
import IProfessional from '@monorepo-zazuu/shared/interfaces/IProfessional'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FaSpinner } from 'react-icons/fa'
import { FiPlus } from 'react-icons/fi'
import { useLocation } from 'react-router'
import { toast } from 'react-toastify'
import Button from '../../components/Button'
import Text from '../../components/Text'
import {
  searchAppointments,
  searchAppointmentsToModal,
  searchProfessionals,
  updateAppointment
} from '../../services/api-graphql-calls'
import orderArrayOfObjects from '../../utils/orderArrayOfObjects'
import {
  appointmentMatchesArea,
  professionalMatchesArea,
  ServiceArea,
  SERVICE_AREA_OPTIONS
} from '../../utils/serviceArea'
import ModalAddAppointmentToProfessional from './ModalAddAppointmentToProfessional'
import ModalAddProfessional from './ModalAddProfessional'
import * as Styles from './styles'
import TableAppointments from './TableAppointments'

interface IAppointmentsByProfessional {
  professional: IProfessional
  appointments: IAppointment[]
}

// As chamadas são Apollo (não axios): a mensagem da API vem em graphQLErrors.
const getErrorMessage = (error: any): string =>
  error?.graphQLErrors?.[0]?.message || error?.message || 'Erro no servidor'

const showErrorToast = (message: string) => {
  toast(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    type: 'error'
  })
}

const Appointments: React.FC = () => {
  const location = useLocation() as { state: { date: string | undefined } }
  const [appointmentsByProfessional, setAppointmentsByProfessional] = useState<
    IAppointmentsByProfessional[]
  >([])
  const [appointments, setAppointments] = useState<IAppointment[]>([])
  const [professionals, setProfessionals] = useState<IProfessional[]>([])
  const [professionalsLoaded, setProfessionalsLoaded] = useState(false)
  const [professionalsLoading, setProfessionalsLoading] = useState(true)
  const [addProfessionalModal, setAddProfessionalModal] = useState(false)
  const [
    addAppointmentToProfessionalId,
    setAddAppointmentToProfessionalId
  ] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  // Filtro só de visualização: não muda o que é buscado nem o que é gravado.
  const [area, setArea] = useState<ServiceArea>('todos')
  // Modal data control (loaded in background, refreshed when the modal opens)
  const [modalAppointments, setModalAppointments] = useState<IAppointment[]>([])
  const [modalDataReady, setModalDataReady] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const modalDataLoadedOnceRef = useRef(false)
  // Sobe a cada alteração local de modalAppointments, para uma busca que já
  // estava em andamento não trazer de volta o estado anterior por cima dela.
  const modalDataVersionRef = useRef(0)

  const fetchModalAppointments = useCallback(
    () =>
      searchAppointmentsToModal({
        where: {
          from: location.state.date,
          to: location.state.date,
          statusNotEqual: -1
        },
        orderBy: [{ date: 'desc' }, { hour: 'asc' }]
      }),
    [location.state.date]
  )

  const unscheduledAppointmentsForModal = useMemo(
    () =>
      modalAppointments.filter(
        ap => !ap.professional_id && appointmentMatchesArea(ap, area)
      ),
    [modalAppointments, area]
  )

  const professionalsForArea = useMemo(
    () => professionals.filter(p => professionalMatchesArea(p, area)),
    [professionals, area]
  )

  useEffect(() => {
    const appointmentsFormatted = appointments.reduce<
      IAppointmentsByProfessional[]
    >((accumulator, appointment) => {
      if (!appointment?.professional_id) {
        return accumulator
      }
      const professional_index = accumulator.findIndex(
        t => t.professional.id === appointment.professional_id
      )
      if (professional_index >= 0) {
        accumulator[professional_index] = {
          ...accumulator[professional_index],
          appointments: [
            ...accumulator[professional_index].appointments,
            appointment
          ]
        }
        return accumulator
      }
      accumulator.push({
        professional: appointment.professional,
        appointments: [appointment]
      })
      return accumulator
    }, [])
    setAppointmentsByProfessional(appointmentsFormatted)
  }, [appointments])

  // Na área escolhida, cada tabela mostra só os atendimentos da área, e o
  // profissional aparece se tem perfil da área ou algum atendimento dela no dia
  // (assim um atendimento alocado para o profissional "errado" continua visível).
  const visibleAppointmentsByProfessional = useMemo(() => {
    if (area === 'todos') {
      return appointmentsByProfessional
    }
    return appointmentsByProfessional.reduce<IAppointmentsByProfessional[]>(
      (accumulator, group) => {
        const appointmentsOfArea = group.appointments.filter(ap =>
          appointmentMatchesArea(ap, area)
        )
        if (
          appointmentsOfArea.length > 0 ||
          professionalMatchesArea(group.professional, area)
        ) {
          accumulator.push({ ...group, appointments: appointmentsOfArea })
        }
        return accumulator
      },
      []
    )
  }, [appointmentsByProfessional, area])

  const handleAddProfessional = useCallback((professional: IProfessional) => {
    setAppointmentsByProfessional(old => [
      ...old,
      {
        professional,
        appointments: []
      }
    ])
  }, [])

  const handleAddToProfessional = useCallback(
    async (app: IAppointment) => {
      try {
        const new_app = await updateAppointment(app)
        setAppointments(oldAppointments => {
          const a = oldAppointments.map(ap =>
            ap.id === new_app.id
              ? {
                  ...ap,
                  ...new_app,
                  professional: professionals.find(
                    p => p.id === new_app.professional_id
                  ) as any
                }
              : ap
          )
          return orderArrayOfObjects(a, 'hour') as IAppointment[]
        })
        // Keep modal data in sync (remove from unscheduled if assigned)
        modalDataVersionRef.current += 1
        setModalAppointments(prev =>
          prev.map(ap => (ap.id === new_app.id ? { ...ap, ...new_app } as IAppointment : ap))
        )
      } catch (error) {
        showErrorToast(getErrorMessage(error))
      }
    },
    [professionals]
  )

  const handleRemoveToProfessional = useCallback(
    async (appointment_id: string) => {
      try {
        const appointment = await updateAppointment({
          id: appointment_id,
          professional_id: null,
          hour: null
        })
        setAppointments(oldAppointments =>
          oldAppointments.map(ap =>
            ap.id === appointment.id ? { ...ap, ...appointment } : ap
          )
        )
        // reflect in modal dataset
        modalDataVersionRef.current += 1
        setModalAppointments(prev =>
          prev.map(ap => (ap.id === appointment.id ? { ...ap, ...appointment } : ap))
        )
      } catch (error) {
        showErrorToast(getErrorMessage(error))
      }
    },
    []
  )

  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true)
      const results = await searchAppointments({
        where: {
          from: location.state.date,
          to: location.state.date,
          statusNotEqual: -1
        },
        orderBy: [{ date: 'desc' }, { hour: 'asc' }]
      })

      setAppointments(results)
    } catch (error) {
      toast('Erro ao carregar agendamentos do dia', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [location.state.date])

  useEffect(() => {
    loadAppointments()
  }, [loadAppointments])


  const loadData = useCallback(async () => {
    try {
      setProfessionalsLoading(true)
      const resultsProfessionals = await searchProfessionals()

      const filteredProfessionals = resultsProfessionals

      setProfessionals(filteredProfessionals)
    } catch (error) {
      toast(error?.response?.data?.message || 'Erro no servidor', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'error'
      })
    } finally {
      setProfessionalsLoaded(true)
      setProfessionalsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])


    // One-time background load for modal data (after professionals load, deferred to ensure table fetch runs first)
    useEffect(() => {
      if (!professionalsLoaded) return
      if (modalDataLoadedOnceRef.current) return
      modalDataLoadedOnceRef.current = true

      const loadModalData = async () => {
        try {
          setModalLoading(true)
          const results = await fetchModalAppointments()
          setModalAppointments(results)
        } catch (error) {
          // Silently fail but still allow UI to proceed
        } finally {
          setModalDataReady(true)
          setModalLoading(false)
        }
      }

      const timer = setTimeout(() => {
        void loadModalData()
      }, 0)

      return () => clearTimeout(timer)
    }, [fetchModalAppointments, professionalsLoaded])

    // O status muda com a página aberta (pedido de reembolso, "reagendar"):
    // ao abrir o modal busca de novo, sem esconder o que já está na tela, para
    // a etiqueta e o bloqueio dos cards refletirem o status atual.
    useEffect(() => {
      if (!addAppointmentToProfessionalId) return
      const version = modalDataVersionRef.current
      let cancelled = false

      fetchModalAppointments()
        .then(results => {
          if (cancelled || version !== modalDataVersionRef.current) return
          setModalAppointments(results)
        })
        .catch(() => {
          // Fica o que já estava; a API continua recusando o que não pode.
        })

      return () => {
        cancelled = true
      }
    }, [addAppointmentToProfessionalId, fetchModalAppointments])
  
  return (
    <Styles.Container>
      <Text
        test="Controle de Agenda"
        size={28}
        color={colors.gray.dark01}
        weight="600"
        align="left"
      />
      <Text
        test="Data do atendimento"
        size={18}
        weight="700"
        color={colors.primary.default}
        align="left"
        marginTop={16}
        marginBottom={8}
      />
      <div>
        <Styles.Row>
          <Text
            test={String(location.state.date)}
            size={24}
            align="center"
            weight="600"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Button
              behavior="secondary"
              text="Adicionar professional"
              style={{ width: '100%' }}
              styleContainer={{ marginLeft: '2rem' }}
              disabled={!modalDataReady}
              onClick={() => {
                if (!modalDataReady) return
                setAddProfessionalModal(true)
              }}
            />
            {(professionalsLoading || modalLoading) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaSpinner size={18} />
                <Text test="Carregando opções..." size={14} />
              </div>
            )}
          </div>
        </Styles.Row>
        <Text
          test="Área"
          size={18}
          weight="700"
          color={colors.primary.default}
          align="left"
          marginTop={16}
          marginBottom={8}
        />
        <Styles.AreaFilter role="group" aria-label="Área">
          {SERVICE_AREA_OPTIONS.map(option => (
            <Button
              key={option.value}
              behavior={area === option.value ? 'primary' : 'secondary'}
              text={option.label}
              aria-pressed={area === option.value}
              onClick={() => setArea(option.value)}
            />
          ))}
        </Styles.AreaFilter>
        <Styles.Divider />
        {loading && (
          <Styles.LoadingDiv>
            <FaSpinner size={40} />
          </Styles.LoadingDiv>
        )}
        {!loading &&
          area !== 'todos' &&
          appointmentsByProfessional.length > 0 &&
          visibleAppointmentsByProfessional.length === 0 && (
            <Text
              test="Nenhum profissional com atendimentos desta área no dia."
              size={16}
              color={colors.gray.dark01}
              align="left"
            />
          )}
        {visibleAppointmentsByProfessional.map((group, index) => (
          <div key={group.professional.id || index}>
            <Text
              test={group.professional.user?.name}
              size={18}
              weight="700"
              color={colors.primary.default}
              align="left"
              marginTop={16}
              marginBottom={8}
            />
            <TableAppointments
              appointments={group.appointments}
              handleRemoveToProfessional={handleRemoveToProfessional}
            />
            <Styles.ContainerAdd
              style={{
                opacity: modalDataReady ? 1 : 0.5,
                pointerEvents: modalDataReady ? 'auto' : 'none',
                cursor: modalDataReady ? 'pointer' : 'not-allowed'
              }}
              onClick={() => {
                if (!modalDataReady) return
                setAddAppointmentToProfessionalId(group.professional.id)
              }}
            >
              <FiPlus name="add" size={24} color={colors.primary.default} />
            </Styles.ContainerAdd>
          </div>
        ))}
      </div>
      {modalDataReady && (
        <>
          <ModalAddProfessional
            visible={addProfessionalModal}
            handleCloseModal={() => setAddProfessionalModal(false)}
            handleAddProfessional={data => handleAddProfessional(data)}
            professionals={professionalsForArea}
          />
          <ModalAddAppointmentToProfessional
            visible={!!addAppointmentToProfessionalId}
            handleCloseModal={() => setAddAppointmentToProfessionalId(undefined)}
            handleAddToProfessional={appointment =>
              handleAddToProfessional(appointment)
            }
            professional_id={addAppointmentToProfessionalId}
            appointments={unscheduledAppointmentsForModal}
          />
        </>
      )}
    </Styles.Container>
  )
}

export default Appointments
